import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, XCircle, Copy, Check, Terminal, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { api } from "@/lib/api";

// ── CopyBox ───────────────────────────────────────────────────────────────────

function CopyBox({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative group rounded-md bg-muted/60 border border-border">
      <pre className="px-4 py-3 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-all pr-10">
        {code}
      </pre>
      <button
        onClick={() => {
          navigator.clipboard.writeText(code);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        className="absolute top-2 right-2 p-1.5 rounded hover:bg-border transition-colors"
        title="Copy"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
    </div>
  );
}

// ── ToggleRow ─────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  settingKey,
}: {
  label: string;
  description: string;
  settingKey: string;
}) {
  const qc = useQueryClient();

  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  const saveMut = useMutation({
    mutationFn: (val: boolean) => api.putSetting(settingKey, val ? "true" : "false"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const currentValue = settings.data?.[settingKey]?.value === "true";

  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={currentValue}
        disabled={saveMut.isPending || settings.isLoading}
        onClick={() => saveMut.mutate(!currentValue)}
        className={[
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent",
          "transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          currentValue ? "bg-emerald-500" : "bg-muted",
        ].join(" ")}
      >
        <span
          aria-hidden
          className={[
            "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0",
            "transition-transform duration-200 ease-in-out",
            currentValue ? "translate-x-4" : "translate-x-0",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

// ── SettingRow ────────────────────────────────────────────────────────────────

function SettingRow({
  label,
  description,
  settingKey,
  type = "text",
  placeholder,
  masked = false,
}: {
  label: string;
  description: string;
  settingKey: string;
  type?: string;
  placeholder?: string;
  masked?: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");

  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
  });

  const saveMut = useMutation({
    mutationFn: () => api.putSetting(settingKey, val),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
      setEditing(false);
    },
  });

  const setting = settings.data?.[settingKey];
  const isConfigured = masked
    ? setting?.configured === true
    : !!(setting?.value);

  return (
    <div className="flex items-start gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          {isConfigured ? (
            <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          ) : (
            <XCircle className="h-4 w-4 text-rose-500 shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        {!masked && setting?.value && !editing && (
          <p className="text-xs font-mono text-muted-foreground mt-1 truncate">{setting.value}</p>
        )}
        {editing && (
          <div className="flex gap-2 mt-2">
            <Input
              type={type}
              placeholder={placeholder}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              className="text-xs h-8 font-mono"
              autoFocus
            />
            <Button size="sm" className="h-8 px-3" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              Save
            </Button>
            <Button size="sm" variant="ghost" className="h-8 px-3" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        )}
      </div>
      {!editing && (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 h-7 text-xs"
          onClick={() => { setVal(""); setEditing(true); }}
        >
          {isConfigured ? "Update" : "Set"}
        </Button>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  const health = useQuery({ queryKey: ["health"], queryFn: api.health, refetchInterval: 30_000, staleTime: 20_000 });
  const catalogHealth = useQuery({ queryKey: ["catalog-health"], queryFn: api.catalogHealth, staleTime: 60_000 });
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.getSettings, staleTime: 60_000 });

  const mcpPath = settings.data?.mcp_server_path?.value ?? "";
  const mcpCmd = mcpPath
    ? `claude mcp add symphony-tools -e SYMPHONY_API_URL=http://localhost:8080 -- node ${mcpPath}`
    : `claude mcp add symphony-tools -e SYMPHONY_API_URL=http://localhost:8080 -- node /path/to/symphony/services/symphony-mcp/dist/index.js`;

  const langchainSnippet = `from langchain_mcp_adapters.client import MultiServerMCPClient

async with MultiServerMCPClient({
    "symphony": {
        "command": "node",
        "args": ["${mcpPath || '/path/to/symphony-mcp/dist/index.js'}"],
        "env": {"SYMPHONY_API_URL": "http://localhost:8080"},
        "transport": "stdio",
    }
}) as client:
    tools = client.get_tools()
    # tools now include: search_catalog, pricing_propose,
    # fetch_icecat_images, curate_product, publish_to_shopify …`;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Integrations, API keys, and Claude Code MCP configuration.
        </p>
      </div>

      {/* System health */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">System health</CardTitle>
        </CardHeader>
        <CardContent>
          {health.data ? (
            <div className="grid grid-cols-3 gap-4 text-sm">
              <Stat label="API" value={health.data.status} ok={health.data.status === "ok"} />
              <Stat label="Database" value={health.data.db} ok={health.data.db === "ok"} />
              <Stat label="Shopify" value={health.data.shopify_env} ok />
            </div>
          ) : (
            <div className="h-12 rounded shimmer" />
          )}
          {catalogHealth.data && (
            <div className="grid grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-border">
              <Stat label="Total SKUs" value={catalogHealth.data.total_skus.toLocaleString()} ok />
              <Stat label="Curated" value={catalogHealth.data.curated.toLocaleString()} ok />
              <Stat label="Published" value={catalogHealth.data.published.toLocaleString()} ok />
              <Stat label="Needs image" value={catalogHealth.data.needs_image.toLocaleString()} ok={catalogHealth.data.needs_image === 0} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Claude Code MCP */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Claude Code MCP
          </CardTitle>
          <CardDescription>
            Wire symphony-tools to the Claude Code CLI so you can query the catalog, run pricing,
            and trigger curation from any Claude conversation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">1. Build the MCP server</p>
            <CopyBox code={`cd services/symphony-mcp && pnpm install && pnpm build`} />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">2. Register with Claude Code CLI</p>
            <CopyBox code={mcpCmd} />
            <p className="text-xs text-muted-foreground">
              Run this once in your terminal. The server persists across Claude Code sessions.
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">3. Set the MCP server path here (for auto-generate commands)</p>
            <SettingRow
              label="MCP server path"
              settingKey="mcp_server_path"
              description="Absolute path to symphony-mcp/dist/index.js — used to generate the claude mcp add command above."
              placeholder="/Users/you/devs/symphony/services/symphony-mcp/dist/index.js"
            />
          </div>
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/20 p-3 text-xs text-emerald-600 dark:text-emerald-400 space-y-1">
            <p className="font-medium">Available tools after registration:</p>
            <p>search_catalog · get_product · pricing_propose · fetch_icecat_images · curate_product · list_curated · get_curated · update_curated · publish_to_shopify · catalog_health</p>
          </div>
        </CardContent>
      </Card>

      {/* LangChain MCP Adapters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            LangChain → MCP (agents-py)
          </CardTitle>
          <CardDescription>
            agents-py connects to symphony-mcp via <code className="font-mono">langchain-mcp-adapters</code>.
            The curation agent gets the same tools as Claude Code.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Install</p>
            <CopyBox code={`cd services/agents-py && uv add langchain-mcp-adapters`} />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Usage in LangGraph agent</p>
            <CopyBox code={langchainSnippet} />
          </div>
          <a
            href="https://github.com/langchain-ai/langchain-mcp-adapters"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            langchain-mcp-adapters on GitHub
          </a>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">API Keys</CardTitle>
          <CardDescription>
            Stored in the DB (not env files). Sensitive values are never returned to the UI — only presence is shown.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <SettingRow
            label="Anthropic API Key"
            settingKey="anthropic_api_key"
            description="Required for agents-py AI curation (separate from claude.ai subscription). Get at console.anthropic.com."
            type="password"
            placeholder="sk-ant-..."
            masked
          />
          <SettingRow
            label="Icecat Username"
            settingKey="icecat_username"
            description="Register at icecat.biz (free Open Icecat tier). Used to auto-fetch product images by MPN."
            placeholder="your-icecat-username"
          />
        </CardContent>
      </Card>

      {/* Automation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Automation</CardTitle>
          <CardDescription>
            Configure autonomous behaviors that run after each AP file import.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleRow
            label="Auto-apply pricing rules"
            settingKey="auto_apply_pricing"
            description="After each TD SYNNEX AP import, recompute proposed_price for all curated products using active pricing rules."
          />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground pb-4">
        Note: .env.local values take precedence over DB settings for agents-py at startup. DB settings drive the UI-configurable workflows.
      </p>
    </div>
  );
}

function Stat({ label, value, ok }: { label: string; value: string | number; ok?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-medium tabular-nums ${ok === false ? "text-rose-500" : ""}`}>{value}</div>
    </div>
  );
}
